import { EdaConnectDialog } from "@features/plugins";
import { pluginSyncApi } from "@features/plugins";
import { pluginKeys } from "@features/plugins/services/queryKeys";
import { useProjects } from "@features/projects";
import PluginIcon from "@mui/icons-material/CableRounded";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useOrgStore } from "@stores/orgStore";
import { useProjectStore } from "@stores/projectStore";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { RackSegment } from "./RackSegment";

/**
 * EDA-plugin presence segment — shows whether the active project has
 * been synced from KiCad (or is awaiting first push). Clicking the
 * segment opens `EdaConnectDialog`, which carries the install link,
 * project ID, and API-key shortcut.
 *
 * Today this is a passive readout fed by `/circuits/plugin/sync-status`
 * (the same endpoint that drives `PluginSyncCard` on the project
 * Settings tab). The IR plan §10 live-sync transport (Phase 17) will
 * later upgrade this to a heartbeat-driven presence indicator with
 * per-plugin chips ("kicad_pcbnew · 2 boards") and conflict pulses.
 */
function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 60_000) return "just now";
  if (diffMs < 3600_000) return `${Math.round(diffMs / 60_000)} min ago`;
  if (diffMs < 86_400_000) return `${Math.round(diffMs / 3600_000)} h ago`;
  if (diffMs < 30 * 86_400_000) {
    return `${Math.round(diffMs / 86_400_000)} d ago`;
  }
  return date.toLocaleDateString();
}

export function EdaPresenceSegment() {
  const [open, setOpen] = useState(false);
  const activeProject = useProjectStore((s) => s.activeProject);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const { data: projects } = useProjects(activeOrgId ?? "personal");

  // Map active project name → UUID using the same join `ProjectSegment`
  // does. The store keys by name (legacy URL key); the sync-status
  // endpoint expects a UUID.
  const projectId = useMemo<string | null>(() => {
    if (!activeProject || !projects) return null;
    const match = projects.find((p) => p.name === activeProject);
    return match?.id ?? null;
  }, [activeProject, projects]);

  const { data: status } = useQuery({
    queryKey: pluginKeys.syncStatus(projectId),
    queryFn: () => {
      if (!projectId) throw new Error("projectId is required");
      return pluginSyncApi.getStatus(projectId);
    },
    enabled: Boolean(projectId),
    // 30s — same cadence as PluginSyncCard. Long enough that a busy
    // rack on the chat page doesn't pound the endpoint.
    staleTime: 30 * 1000,
  });

  const synced = (status?.push_count ?? 0) > 0;

  return (
    <>
      <RackSegment label="EDA" empty={!synced} onClick={() => setOpen(true)}>
        <Stack direction="row" spacing={0.875} alignItems="center">
          <Box
            aria-hidden
            sx={(t) => ({
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: synced
                ? t.palette.success.main
                : t.palette.text.disabled,
              boxShadow: synced
                ? `0 0 0 2px ${alpha(t.palette.success.main, 0.18)}`
                : "none",
              flexShrink: 0,
            })}
          />
          <PluginIcon
            sx={{
              color: synced ? "success.main" : "text.secondary",
              fontSize: 14,
            }}
          />
          <Typography
            sx={{
              fontFamily: (t) => t.fonts.mono,
              fontSize: 11,
              fontWeight: 600,
              color: synced ? "text.primary" : "text.secondary",
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {synced
              ? `KiCad · ${formatRelative(status?.last_pushed_at ?? null)}`
              : projectId
                ? "KiCad · not connected"
                : "Connect KiCad"}
          </Typography>
        </Stack>
      </RackSegment>
      <EdaConnectDialog
        open={open}
        onClose={() => setOpen(false)}
        projectId={projectId}
      />
    </>
  );
}
